import type { BoardScene } from './boardScene';

/**
 * Pointer input for the board: tap, drag-pan, pinch zoom and wheel zoom.
 *
 * Gesture discrimination is the whole point of this module. A tap only activates an arrow
 * when the pointer stayed within a small movement threshold, no second pointer joined, and
 * the gesture was not a continuation of a pan or pinch. Anything else moves the camera.
 */

export interface BoardInputCallbacks {
  onActivate(arrowId: number): void;
  onPress?(arrowId: number): void;
  onCameraChange?(): void;
}

export interface BoardInputOptions {
  /** Movement in CSS pixels beyond which a gesture stops being a tap. */
  readonly tapMoveThreshold?: number;
  /** Maximum press duration in ms that still counts as a tap. */
  readonly tapDurationLimit?: number;
  /** Maximum gap in ms between taps for a double tap. */
  readonly doubleTapWindow?: number;
}

interface ActivePointer {
  readonly pointerId: number;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  startTime: number;
  moved: boolean;
  arrowId: number;
}

const WHEEL_ZOOM_STEP = 0.0016;

export class BoardInput {
  private readonly pointers = new Map<number, ActivePointer>();
  private readonly tapMoveThreshold: number;
  private readonly tapDurationLimit: number;
  private readonly doubleTapWindow: number;

  private gestureIsMultiTouch = false;
  private pinchStartDistance = 0;
  private pinchStartScale = 1;
  private lastTapTime = 0;
  private lastTapX = 0;
  private lastTapY = 0;
  private disposed = false;

  constructor(
    private readonly scene: BoardScene,
    private readonly callbacks: BoardInputCallbacks,
    options: BoardInputOptions = {},
  ) {
    this.tapMoveThreshold = options.tapMoveThreshold ?? 10;
    this.tapDurationLimit = options.tapDurationLimit ?? 900;
    this.doubleTapWindow = options.doubleTapWindow ?? 320;

    const canvas = scene.canvas;
    canvas.addEventListener('pointerdown', this.handlePointerDown);
    canvas.addEventListener('pointermove', this.handlePointerMove);
    canvas.addEventListener('pointerup', this.handlePointerUp);
    canvas.addEventListener('pointercancel', this.handlePointerCancel);
    canvas.addEventListener('pointerleave', this.handlePointerCancel);
    canvas.addEventListener('wheel', this.handleWheel, { passive: false });
    canvas.addEventListener('contextmenu', this.handleContextMenu);
  }

  private localPosition(event: PointerEvent | WheelEvent): { x: number; y: number } {
    const rect = this.scene.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private handlePointerDown = (event: PointerEvent): void => {
    if (this.disposed) return;
    // Ignore secondary mouse buttons: right-click should not activate arrows.
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const { x, y } = this.localPosition(event);
    // Capture is best effort: browsers throw when the pointer id is no longer active, and a
    // failed capture only costs us tracking a pointer that left the canvas.
    try {
      this.scene.canvas.setPointerCapture?.(event.pointerId);
    } catch {
      // Ignored deliberately.
    }

    const pointer: ActivePointer = {
      pointerId: event.pointerId,
      startX: x,
      startY: y,
      lastX: x,
      lastY: y,
      startTime: performance.now(),
      moved: false,
      arrowId: this.scene.hitTest(x, y),
    };
    this.pointers.set(event.pointerId, pointer);

    if (this.pointers.size === 2) {
      this.beginPinch();
    } else if (this.pointers.size === 1 && pointer.arrowId >= 0) {
      // Acknowledge the press immediately, before any rule evaluation.
      this.callbacks.onPress?.(pointer.arrowId);
    }
  };

  private beginPinch(): void {
    this.gestureIsMultiTouch = true;
    const [first, second] = [...this.pointers.values()];
    this.pinchStartDistance = distance(first.lastX, first.lastY, second.lastX, second.lastY);
    this.pinchStartScale = this.scene.camera.scale;
  }

  private handlePointerMove = (event: PointerEvent): void => {
    const pointer = this.pointers.get(event.pointerId);
    if (pointer === undefined) return;

    const { x, y } = this.localPosition(event);
    const dx = x - pointer.lastX;
    const dy = y - pointer.lastY;
    pointer.lastX = x;
    pointer.lastY = y;

    if (
      Math.abs(x - pointer.startX) > this.tapMoveThreshold ||
      Math.abs(y - pointer.startY) > this.tapMoveThreshold
    ) {
      pointer.moved = true;
    }

    if (this.pointers.size >= 2) {
      this.updatePinch();
      return;
    }
    if (pointer.moved) {
      this.scene.camera.panBy(dx, dy);
      this.scene.applyCamera();
      this.callbacks.onCameraChange?.();
    }
  };

  private updatePinch(): void {
    const pointers = [...this.pointers.values()];
    if (pointers.length < 2) return;
    const [first, second] = pointers;
    const currentDistance = distance(first.lastX, first.lastY, second.lastX, second.lastY);
    if (this.pinchStartDistance <= 0 || currentDistance <= 0) return;

    const midX = (first.lastX + second.lastX) / 2;
    const midY = (first.lastY + second.lastY) / 2;
    this.scene.camera.zoomTo(
      (this.pinchStartScale * currentDistance) / this.pinchStartDistance,
      midX,
      midY,
    );
    this.scene.applyCamera();
    this.callbacks.onCameraChange?.();
  }

  private handlePointerUp = (event: PointerEvent): void => {
    const pointer = this.pointers.get(event.pointerId);
    if (pointer === undefined) return;
    this.pointers.delete(event.pointerId);
    try {
      this.scene.canvas.releasePointerCapture?.(event.pointerId);
    } catch {
      // Ignored deliberately: the capture may already have been released.
    }

    const wasMultiTouch = this.gestureIsMultiTouch;
    if (this.pointers.size === 0) this.gestureIsMultiTouch = false;

    // A pinch or a pan never activates an arrow, and neither does the lingering finger of
    // a two-finger gesture.
    if (wasMultiTouch || pointer.moved) return;
    if (performance.now() - pointer.startTime > this.tapDurationLimit) return;

    const now = performance.now();
    const isDoubleTap =
      now - this.lastTapTime < this.doubleTapWindow &&
      Math.abs(pointer.lastX - this.lastTapX) < 40 &&
      Math.abs(pointer.lastY - this.lastTapY) < 40;

    if (isDoubleTap) {
      this.lastTapTime = 0;
      this.scene.fit();
      this.callbacks.onCameraChange?.();
      return;
    }

    this.lastTapTime = now;
    this.lastTapX = pointer.lastX;
    this.lastTapY = pointer.lastY;

    // Re-test on release: the camera may have moved, and the arrow may already be gone.
    const arrowId = this.scene.hitTest(pointer.lastX, pointer.lastY);
    if (arrowId >= 0) this.callbacks.onActivate(arrowId);
  };

  private handlePointerCancel = (event: PointerEvent): void => {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.delete(event.pointerId);
    if (this.pointers.size === 0) this.gestureIsMultiTouch = false;
  };

  private handleWheel = (event: WheelEvent): void => {
    // The board owns wheel gestures over its own canvas only; the page still scrolls
    // normally everywhere else.
    event.preventDefault();
    const { x, y } = this.localPosition(event);
    const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
    const factor = 1 - delta * WHEEL_ZOOM_STEP;
    this.scene.camera.zoomBy(factor, x, y);
    this.scene.applyCamera();
    this.callbacks.onCameraChange?.();
  };

  private handleContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  dispose(): void {
    this.disposed = true;
    const canvas = this.scene.canvas;
    canvas.removeEventListener('pointerdown', this.handlePointerDown);
    canvas.removeEventListener('pointermove', this.handlePointerMove);
    canvas.removeEventListener('pointerup', this.handlePointerUp);
    canvas.removeEventListener('pointercancel', this.handlePointerCancel);
    canvas.removeEventListener('pointerleave', this.handlePointerCancel);
    canvas.removeEventListener('wheel', this.handleWheel);
    canvas.removeEventListener('contextmenu', this.handleContextMenu);
    this.pointers.clear();
  }
}

function distance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}
